import { Env } from '@levery/apps'
import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { initializeColorScheme, toggleColorScheme } from '~/lib/ColorScheme.js'
import { initialize as initializeLevery } from '~/lib/Levery.js'
import './styles.css'

async function main() {
  initializeColorScheme()
  await initializeLevery()
  const [{ App }, Router] = await Promise.all([
    import('./App.js'),
    import('~/lib/Router.js'),
  ])

  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: 'https://1b4e28921c688e2b03d1b63f8d018913@o4509056062849024.ingest.us.sentry.io/4509080371724288',
      environment: Env.get(),
      integrations: [
        Sentry.replayIntegration(),
        Sentry.tanstackRouterBrowserTracingIntegration(Router.router),
      ],
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
    })
  }

  const rootElement = document.querySelector('div#root')

  if (!rootElement) throw new Error('Root element not found')

  createRoot(rootElement, {
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack)
    }),
  }).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )

  document.addEventListener('keydown', (event) => {
    // ⌥ + 1: light/dark mode
    if (event.altKey && event.code === 'Digit1') {
      toggleColorScheme()
    }

    // ⌥ + 2: toggle dialog mode
    if (event.altKey && event.code === 'Digit2') {
      document.documentElement.toggleAttribute('data-dialog')
    }

    // ⌥ + h: hide dev tools
    if (event.altKey && event.code === 'KeyH') {
      const devToolsElement = document.querySelector(
        'button[aria-label="Open TanStack Router Devtools"]',
      )
      if (devToolsElement) devToolsElement.hidden = !devToolsElement.hidden

      const devTools = document.querySelector('div[data-item="dev-tools"]')
      if (devTools) devTools.hidden = !devTools.hidden
    }
  })
}

void main()

declare module 'react' {
  interface CSSProperties {
    [key: `--${string}`]: string | number
  }
}
